import { Context as AuthorizationContext } from 'authorization/Context';
import { Clinician } from 'clinician';
import { getClinicianId, getClinicianPractitioner } from 'clinician/helpers';
import { ERROR_ACCOUNT_NOT_LINKED } from 'clinician-config/constants';
import React, { useContext, useEffect, useMemo, useReducer } from 'react';
import { useClinicianQuery } from 'routing/Context/useClinicianQuery';
import { defaultState, reducer } from 'routing/Schedule/reducer';
import Error from 'styleguide/Error';
import Loading from 'styleguide/Loading';
import Context from './Context';

type Props = {
  children?: React.ReactNode;
  isMySchedule?: boolean;
};

const Provider: React.FC<Props> = ({ children, isMySchedule = false }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...defaultState,
    isMySchedule,
    currentDate: new Date().toISOString(),
  });
  const { user } = useContext(AuthorizationContext);

  // Safe parsing of clinician from localStorage with error handling
  const scheduleClinicianId = useMemo(() => {
    try {
      const storedValue = localStorage.getItem(
        `PCC:${user?.id}:SchedulingClinician`
      );
      if (!storedValue) return null;

      // Try to parse as JSON first (new format)
      try {
        const parsedClinician = JSON.parse(storedValue);
        return getClinicianId(parsedClinician);
      } catch {
        // Fallback: if it's not JSON, assume it's just the ID (old format)
        return storedValue;
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: reason here
      console.warn('Error parsing clinician from localStorage:', error);
      // Clear invalid data
      try {
        localStorage.removeItem(`PCC:${user?.id}:SchedulingClinician`);
      } catch {}
      return null;
    }
  }, [user?.id]);

  const queryCliniciansIds =
    scheduleClinicianId && scheduleClinicianId !== user?.id
      ? `${user?.id},${scheduleClinicianId}`
      : user?.id;

  const { loading, error, clinicians } = useClinicianQuery({
    clinicianId: queryCliniciansIds,
  });

  const clinician = clinicians.find(
    (clin) => getClinicianPractitioner(clin)?.id === user?.id
  ) as Clinician;

  const scheduleClinician = clinicians.find(
    (clin) => getClinicianPractitioner(clin)?.id === scheduleClinicianId
  ) as Clinician;

  useEffect(() => {
    if (isMySchedule) {
      dispatch({ type: 'setClinician', clinician });
    } else {
      dispatch({ type: 'setClinician', clinician: scheduleClinician });
    }
  }, [clinician, scheduleClinician, isMySchedule]);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
    }),

    [state]
  );

  if (error) {
    return <Error text={error?.message} />;
  }

  if (loading) {
    return <Loading />;
  }

  if (isMySchedule && !getClinicianId(state?.clinician)) {
    return <Error text={ERROR_ACCOUNT_NOT_LINKED} />;
  }

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default Provider;
